import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { EMPTY, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './helpers/api.service';
import { apiResourcesV1 } from './helpers/api-resources';
import { TokenStorageService } from './helpers/token.service';
import { AuthResponse } from '../interfaces/auth-response.interface';
import { DiscoveryData } from '../interfaces/discovery-data.interface';
const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable()
export class AuthService {
  private refreshTokenTimeout;
  private tokenRefreshed$ = new Subject<void>();

  constructor(
    private http: ApiService,
    private tokenStorage: TokenStorageService,
  ) {}

  // Observable для сповіщення про оновлення токена
  get tokenRefreshed(): Observable<void> {
    return this.tokenRefreshed$.asObservable();
  }

  login(data: any): Observable<AuthResponse> {
    return this.http.post(apiResourcesV1.signIn, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  logOutDeviceSession(sessionId: string, data: any): Observable<any> {
    return this.http
      .post(`${apiResourcesV1.accountSessions}/${sessionId}`, data)
      .pipe(
        map((res: any) => {
          return res;
        }),
      );
  }

  logOutAllDeviceSession(data: any): Observable<any> {
    return this.http.post(`${apiResourcesV1.accountSessions}`, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  reset(data): Observable<any> {
    return this.http.post(apiResourcesV1.forgotPassword, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  changePassword(data: string): Observable<any> {
    return this.http.post(apiResourcesV1.resetPassword, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  changeRole(data): Observable<any> {
    return this.http.post(apiResourcesV1.changeRole, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  registration(data: string): Observable<any> {
    return this.http.post(`${apiResourcesV1.registration}`, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  getProfile(): Observable<any> {
    return this.http.get(apiResourcesV1.profile).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  getAccountSessions(): Observable<any> {
    return this.http.get(apiResourcesV1.accountSessions).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  logOutAllDeviceSessions(data: any): Observable<any> {
    return this.http.delete(apiResourcesV1.accountSessions, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  setUser() {
    sessionStorage.setItem('authorization', 'true');
  }

  clearStorage() {
    sessionStorage.clear();
    localStorage.clear();
  }
  logout(): Observable<any> {
    return this.http.post(apiResourcesV1.signOut, {}).pipe(
      map((res: any) => {
        this.stopRefreshTokenTimer();
        if (res.redirect_url) {
          setTimeout(() => {
            globalThis.location.href = res.redirect_url;
          }, 1000);
        }

        return res;
      }),
    );
  }
  refreshToken(): Observable<any> {
    const refresh = this.tokenStorage.getRefreshToken();
    const token = this.tokenStorage.getToken();
    if (!token) {
      return EMPTY;
    }
    return this.http
      .post(
        apiResourcesV1.refreshToken,
        {
          refresh_token: refresh,
          access_token: token,
        },
        httpOptions,
      )
      .pipe(
        map((res: any) => {
          if (res?.token?.access_token) {
            this.tokenStorage.saveToken(res.token.access_token);
          } else {
            this.tokenStorage.removeToken();
          }
          if (res?.token?.refresh_token) {
            this.tokenStorage.saveRefreshToken(res.token.refresh_token);
          } else {
            this.tokenStorage.removeRefreshToken();
          }
          this.tokenStorage.saveExpireRefreshToken(res.token.refresh_expire);
          this.tokenStorage.saveExpireToken(res.token.access_expire);
          this.tokenStorage.saveUser(res.token);
          if (res?.token?.refresh_token) {
            setTimeout(() => {
              this.startRefreshTokenTimer();
            }, 200);
          }

          this.tokenRefreshed$.next();

          return res;
        }),
      );
  }
  sendTwoFactorCode(data: any): Observable<any> {
    return this.http.post(apiResourcesV1.sendTwoFactorCode, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  discovery(data: any): Observable<DiscoveryData> {
    return this.http.post(apiResourcesV1.discovery, data).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }

  ssoExchange(): Observable<AuthResponse> {
    return this.http.post(apiResourcesV1.ssoExchange, {}).pipe(
      map((res: any) => {
        return res;
      }),
    );
  }
  // helper methods
  startRefreshTokenTimer() {
    const expires = new Date(Number(this.tokenStorage.getExpireToken()) * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(
      () => this.refreshToken().subscribe(),
      timeout,
    );
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }
}
